import type {NormalizedCacheObject} from '@apollo/client';
import {
    type ApolloClientProxy,
    EntityBaseGqlService,
    type GetDownloadLinkRequestPayload,
    type ServiceResponsePayload,
} from '@igp/shared/api';
import type {
    CreateRepaymentLogInput,
    EditRepaymentLogInput,
    Mutation,
    MutationCreateRepaymentLogArgs,
    MutationEditRepaymentLogArgs,
    MutationRemoveRepaymentLogArgs,
    Query,
    QueryGetRepaymentLogsArgs,
    RepaymentLog,
    Subscription,
} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import type {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {type IRepaymentLogRequestBuilder, RepaymentLogRequestBuilder} from './builder';
import type {RepaymentLogDownloadFields, RepaymentLogErrorCodes, RepaymentLogFilterKeys, RepaymentLogQueryFields} from './types';

export interface IRepaymentLogService {
    create: (log: CreateRepaymentLogInput) => Observable<ServiceResponsePayload<CreateRepaymentLogInput, RepaymentLog>>;
    edit: (log: EditRepaymentLogInput) => Observable<ServiceResponsePayload<EditRepaymentLogInput, RepaymentLog>>;
    remove: (repayment_id: string) => Observable<ServiceResponsePayload<string, string>>;
}

@injectable()
export class RepaymentLogService
    extends EntityBaseGqlService<
        EntityType,
        Query,
        QueryGetRepaymentLogsArgs,
        RepaymentLogQueryFields,
        Mutation,
        Subscription,
        RepaymentLogErrorCodes,
        RepaymentLogFilterKeys
    >
    implements IRepaymentLogService
{
    private readonly _requestBuilder: IRepaymentLogRequestBuilder;
    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer<RepaymentLogErrorCodes>
    ) {
        const builder = new RepaymentLogRequestBuilder();
        super(client, builder, messageLocalizer);
        this._requestBuilder = builder;
    }

    get(
        requestPayload: EntityFetchRequestPayload<EntityType, RepaymentLogQueryFields>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, RepaymentLog>> {
        return this.fetchData(requestPayload).pipe(
            map(r => ({
                ...r,
                requestPayload,
                responsePayload: {
                    items: r.responsePayload.getRepaymentLogs?.items ?? [],
                    total: r.responsePayload.getRepaymentLogs?.total_count ?? 0,
                },
            }))
        );
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<RepaymentLogDownloadFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<RepaymentLogDownloadFields>, string>> {
        const request = this._requestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: response?.responsePayload?.exportRepaymentLogs?.url,
            }))
        );
    }

    protected getErrorSeverity(code: RepaymentLogErrorCodes): number {
        const errorCodeSeverityMapping: Record<RepaymentLogErrorCodes, number> = {
            REPAYMENT_LOG_NOT_FOUND: 1,
            PLAYER_IS_NOT_MASTER_AGENT: 1,
            AGENT_IS_NOT_DOWNLINE_OF_MASTER_AGENT: 1,
        };
        return errorCodeSeverityMapping[code] ?? 0;
    }
    create(log: CreateRepaymentLogInput): Observable<ServiceResponsePayload<CreateRepaymentLogInput, RepaymentLog>> {
        return this._service.mutate<MutationCreateRepaymentLogArgs>(this._requestBuilder.getCreateMutation(), {log}).pipe(
            map(response => ({
                ...response,
                requestPayload: log,
                responsePayload: response?.responsePayload?.createRepaymentLog,
            }))
        );
    }
    edit(log: EditRepaymentLogInput): Observable<ServiceResponsePayload<EditRepaymentLogInput, RepaymentLog>> {
        return this._service.mutate<MutationEditRepaymentLogArgs>(this._requestBuilder.getEditMutation(), {log}).pipe(
            map(response => ({
                ...response,
                requestPayload: log,
                responsePayload: response?.responsePayload?.editRepaymentLog,
            }))
        );
    }
    remove(repayment_id: string): Observable<ServiceResponsePayload<string, string>> {
        return this._service.mutate<MutationRemoveRepaymentLogArgs>(this._requestBuilder.getRemoveMutation(), {repayment_id}).pipe(
            map(response => ({
                ...response,
                requestPayload: repayment_id,
                responsePayload: response?.responsePayload?.removeRepaymentLog,
            }))
        );
    }
}
